import { createContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  traduzir,
  formatarData,
  formatarDataHora,
  formatarNumero,
  formatarMoeda,
  nomeMes,
  type Idioma,
  type Variaveis,
} from '@/lib/i18n';

const CHAVE_ARMAZENAMENTO = 'idioma';

export interface I18nContextType {
  idioma: Idioma;
  setIdioma: (idioma: Idioma) => void;
  alternarIdioma: () => void;
  /** `t('apreciacoes.lista.titulo')` — ver src/lib/i18n/README.md. */
  t: (chave: string, variaveis?: Variaveis) => string;
  formatarData: (valor: Date | string | number | null | undefined, opcoes?: Intl.DateTimeFormatOptions) => string;
  formatarDataHora: (valor: Date | string | number | null | undefined, opcoes?: Intl.DateTimeFormatOptions) => string;
  formatarNumero: (valor: number | null | undefined, opcoes?: Intl.NumberFormatOptions) => string;
  formatarMoeda: (valor: number | null | undefined) => string;
  nomeMes: (mes: number, formato?: 'long' | 'short') => string;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

function idiomaInicial(): Idioma {
  const salvo = localStorage.getItem(CHAVE_ARMAZENAMENTO);
  if (salvo === 'pt' || salvo === 'en') return salvo;
  // Sem preferência salva: o time é brasileiro e o conteúdo do banco está em
  // português, então o padrão é pt mesmo em navegador configurado em inglês.
  return 'pt';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [idioma, setIdiomaState] = useState<Idioma>(idiomaInicial);

  useEffect(() => {
    localStorage.setItem(CHAVE_ARMAZENAMENTO, idioma);
    document.documentElement.lang = idioma === 'en' ? 'en' : 'pt-BR';
  }, [idioma]);

  const setIdioma = useCallback((novo: Idioma) => setIdiomaState(novo), []);
  const alternarIdioma = useCallback(
    () => setIdiomaState((atual) => (atual === 'pt' ? 'en' : 'pt')),
    []
  );

  const valor = useMemo<I18nContextType>(
    () => ({
      idioma,
      setIdioma,
      alternarIdioma,
      t: (chave, variaveis) => traduzir(idioma, chave, variaveis),
      formatarData: (valor, opcoes) => formatarData(idioma, valor, opcoes),
      formatarDataHora: (valor, opcoes) => formatarDataHora(idioma, valor, opcoes),
      formatarNumero: (valor, opcoes) => formatarNumero(idioma, valor, opcoes),
      formatarMoeda: (valor) => formatarMoeda(idioma, valor),
      nomeMes: (mes, formato) => nomeMes(idioma, mes, formato),
    }),
    [idioma, setIdioma, alternarIdioma]
  );

  return <I18nContext.Provider value={valor}>{children}</I18nContext.Provider>;
}
